import { Update } from '@mui/icons-material';
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';

import { getConsultorResponse, getBusinessByIdentifier } from '../../../../../lib/api';
import { getCachedSuperDebt } from '../../../../../lib/api/integrations/debt';
import CustomButton from '../../../../../lib/designSystem/Button';
import CenteredCircularProgress from '../../../../../lib/designSystem/CircularProgress';
import HeaderWithActions from '../../../../../lib/designSystem/HeaderWithActions';
import EnhancedTable from '../../../../../lib/designSystem/Table';
import {
  formatDate,
  showAlert,
  validatePermissions,
} from '../../../../../lib/functions/utils';
import { AuthState } from '../../../../../services/redux/authSlice';

const SuperDebt = (props: any) => {
  const { businessId } = props;
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);
  const [lastUpdate, setLastUpdate] = useState<any>(null);
  const [businessName, setBusinessName] = useState<string>('');
  const [superDebt, setSuperDebt] = useState<any>({
    identifier: businessId,
    headers: [],
    rows: [],
  });
  const { id } = useParams<{
    id: string;
    businessId: string;
    page: string;
  }>();
  const user = useSelector((state: { auth: AuthState }) => state.auth.user);

  const businessIdentifier = id || businessId;

  const fetchBusinessData = async () => {
    try {
      const businessData = await getBusinessByIdentifier(dispatch, {
        identifier: businessIdentifier,
      });
      if (businessData && businessData.name) {
        setBusinessName(businessData.name);
      }
    } catch (error) {
      console.log('Error fetching business data:', error);
      // Don't show error alert for business name fetch as it's not critical
    }
  };

  const fetchCachedSuperDebt = async () => {
    setLoading(true);
    try {
      const superDebt = await getCachedSuperDebt(dispatch, businessIdentifier);
      setSuperDebt(superDebt.data);
      setLastUpdate(superDebt.lastUpdate);
    } catch (error) {
      console.log(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    setLoading(false);
  };

  const fetchSuperDebt = async () => {
    setLoading(true);
    try {
      const responseConsult = await getConsultorResponse(dispatch, {
        type: 'rut',
        consult: 'superDebt',
        payload: {
          identifier: businessIdentifier,
        },
      });
      setSuperDebt(responseConsult.data);
      setLastUpdate(new Date());
      setOpen(false);
    } catch (error) {
      console.log(error);
      showAlert(dispatch, 'error', 'Ha ocurrido un error');
    }
    setLoading(false);
  };

  const canUpdate = () => validatePermissions(user, 'can_update_debt');

  useEffect(() => {
    fetchCachedSuperDebt();
    fetchBusinessData();
  }, []);

  if (loading) {
    return <CenteredCircularProgress relative />;
  }

  return (
    <div>
      <HeaderWithActions
        item={
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <Typography>
              {lastUpdate
                ? `Última actualización: ${formatDate(lastUpdate)}`
                : 'No se ha actualizado la deuda'}
            </Typography>
            <IconButton
              disabled={!canUpdate()}
              onClick={() => setOpen(true)}
              sx={{ marginLeft: 1 }}
            >
              <Update />
            </IconButton>
          </div>
        }
        title="Deuda de la empresa."
        variant="body1"
      />
      {superDebt.rows.length > 0 && (
        <div
          style={
            businessId
              ? { maxHeight: '400px', overflow: 'auto' }
              : { paddingBottom: '20px' }
          }
        >
          <EnhancedTable
            exportToFile="xlsx"
            dataHeaders={superDebt.headers}
            data={superDebt.rows}
            rowsPerPageDefault={20}
            exportToPdf={true}
            pdfTitle={`Deuda - ${businessName ? `${businessName} - ` : ''}RUT: ${businessIdentifier}`}
          />
        </div>
      )}
      {superDebt.rows.length === 0 && (
        <Typography fontWeight="bold">
          No se encontraron resultados, actualiza la deuda.
        </Typography>
      )}
      <Dialog open={open} onClose={() => setOpen(false)}>
        <DialogTitle>Actualizar deuda</DialogTitle>
        <DialogContent>
          <Typography>
            Recuerda que esta acción consume créditos de tu plan.
          </Typography>
        </DialogContent>
        <DialogActions>
          <CustomButton color="secondary" onClick={() => setOpen(false)}>
            Cancelar
          </CustomButton>
          <CustomButton onClick={() => fetchSuperDebt()}>
            Actualizar
          </CustomButton>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default SuperDebt;
